import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  Timestamp,
  doc,
  updateDoc 
} from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { UserRole } from '../lib/types/auth';

export interface ChatUser {
  email: string;
  roles: string[];
  displayName?: string;
  photoURL?: string;
}

export interface Chat {
  id: string;
  name?: string;
  participants: string[];
  lastMessage?: string;
  lastMessageSender?: string;
  updatedAt: Timestamp;
  isPrivate: boolean;
  createdBy: string;
  createdAt: Timestamp;
}

export interface ChatAttachment {
  url: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadPath: string;
}

export interface Message {
  id: string;
  sender: string;
  text: string;
  imageUrl?: string;
  attachment?: ChatAttachment;
  timestamp: Timestamp;
}

const ALLOWED_ROLES = [
  UserRole.MasterAdmin,
  UserRole.CSAdmin,
  UserRole.MarketingAdmin,
  UserRole.Template,
  UserRole.SalesRep
];

export const chatService = {
  // Validate if user exists and has appropriate role for FIN Chats
  async validateChatUser(email: string): Promise<ChatUser | null> {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email.toLowerCase()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const userData = querySnapshot.docs[0].data();
      const userRoles = userData.roles || [];

      // Check if user has any of the allowed roles
      const hasValidRole = userRoles.some((role: string) => 
        ALLOWED_ROLES.includes(role as UserRole)
      );

      if (!hasValidRole) {
        return null;
      }

      return {
        email: email.toLowerCase(),
        roles: userRoles,
        displayName: userData.displayName,
        photoURL: userData.photoURL
      };
    } catch (error) {
      console.error('Error validating chat user:', error);
      return null;
    }
  },

  // Create a new chat between users
  async createChat(participants: string[], createdBy: string, isPrivate: boolean = true): Promise<string | null> {
    try {
      const chatData = {
        participants: participants.map(p => p.toLowerCase()),
        createdBy,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        lastMessage: '',
        isPrivate
      };

      const docRef = await addDoc(collection(db, 'chats'), chatData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating chat:', error);
      return null;
    }
  },

  // Send a message and update the parent chat
  async sendMessage(chatId: string, sender: string, text: string, imageUrl?: string): Promise<boolean> {
    try {
      const message = {
        sender,
        text: text.trim(),
        ...(imageUrl && { imageUrl }),
        timestamp: Timestamp.now()
      };

      // Add the message to the messages subcollection
      await addDoc(collection(db, 'chats', chatId, 'messages'), message);

      // Update the parent chat document
      const chatRef = doc(db, 'chats', chatId);
      const lastMessage = imageUrl ? (text.trim() || '📷 Image') : text.trim();
      await updateDoc(chatRef, {
        lastMessage,
        lastMessageSender: sender,
        updatedAt: Timestamp.now()
      });

      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    }
  },

  // Send a message with file attachment
  async sendMessageWithAttachment(chatId: string, sender: string, text: string, attachment: ChatAttachment): Promise<boolean> {
    try {
      const message = {
        sender,
        text: text.trim(),
        attachment,
        timestamp: Timestamp.now()
      };

      // Add the message to the messages subcollection
      await addDoc(collection(db, 'chats', chatId, 'messages'), message);

      // Update the parent chat document
      const chatRef = doc(db, 'chats', chatId);
      const lastMessage = `📎 ${attachment.fileName}${text.trim() ? ': ' + text.trim() : ''}`;
      await updateDoc(chatRef, {
        lastMessage,
        lastMessageSender: sender,
        updatedAt: Timestamp.now()
      });

      return true;
    } catch (error) {
      console.error('Error sending message with attachment:', error);
      return false;
    }
  },

  // Format display name for chat participants
  formatDisplayName(participants: string[], currentUserEmail: string, chatName?: string): string {
    if (chatName) return chatName;
    
    const otherParticipants = participants.filter(p => p !== currentUserEmail);
    if (otherParticipants.length === 0) return 'You';
    if (otherParticipants.length === 1) return otherParticipants[0];
    return otherParticipants.join(', ');
  },

  // Find the one-on-one chat between two people, creating it if it doesn't exist yet
  async findOrCreateDirectChat(myEmail: string, myUid: string, otherEmail: string): Promise<string> {
    const me = myEmail.toLowerCase();
    const other = otherEmail.toLowerCase();
    const emails = Array.from(new Set([myEmail, me]));
    for (const email of emails) {
      const snap = await getDocs(query(collection(db, 'chats'), where('participants', 'array-contains', email)));
      const match = snap.docs.find((d) => {
        const p: string[] = (d.data().participants || []).map((e: string) => e.toLowerCase());
        return d.data().isPrivate !== false && p.length === 2 && p.includes(me) && p.includes(other);
      });
      if (match) return match.id;
    }
    const ref = await addDoc(collection(db, 'chats'), {
      participants: [me, other],
      createdBy: myUid,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      lastMessage: '',
      isPrivate: true,
    });
    return ref.id;
  }
};